import type { Cube, Meta, TCubeMemberType } from '@cubejs-client/core'
import { z } from 'zod'

import type { FilterFieldKind } from '@/components/filters/column-filter'
import { NAMED_DEFAULT_RANGES } from '@/lib/date-range'
import { KANTAR_VIEW } from '@/lib/kantar-dia'
import {
  formatBoolean,
  formatCubeDateTime,
  formatDateOnly,
  formatNumberPattern,
  formatText,
  RELATIVE_RANGE_PATTERN,
  withCurrency,
} from '@/lib/format'

/**
 * Rapor meta sözleşmesi (docs/contracts.md): her public Cube view'ının `meta` bloğu
 * o raporun ekran tanımıdır. Frontend rapor sayfalarını, route'ları ve menüyü
 * yalnızca buradan kurar — yeni rapor = yeni `.yml`, frontend kodu değil.
 *
 * Meta'daki üye adları kısadır (`tarih`); burada view adıyla nitelenir
 * (`Tartim.tarih`) ve view'da gerçekten var olup olmadıkları doğrulanır. Tip
 * güvenliği artık derleme zamanında değil, bu doğrulamada.
 */

export const GRANULARITIES = ['day', 'week', 'month', 'quarter', 'year'] as const
export type Granularity = (typeof GRANULARITIES)[number]

export const filterSchema = z.strictObject({
  member: z.string(),
  type: z.enum(['dateRange', 'multiSelect', 'boolean']),
  /**
   * Adres çubuğunda filtre yokken uygulanan değer. dateRange: göreli aralık
   * (`-1d` bugün, `-1y` son 1 yıl, `+2y` önümüzdeki 2 yıl; birim d/m/y) ya da `thisMonth`
   * (bu ayın başından bugüne). multiSelect: seçili değerler.
   */
  default: z
    .union([
      z.string().regex(RELATIVE_RANGE_PATTERN),
      z.enum(NAMED_DEFAULT_RANGES),
      z.array(z.string()),
    ])
    .optional(),
})

const seriesSchema = z.strictObject({
  measure: z.string(),
  type: z.enum(['bar', 'line']),
  axis: z.enum(['left', 'right']).default('left'),
})

const baseSchema = z.strictObject({
  route: z.string().startsWith('/'),
  nav: z.strictObject({
    group: z.string().min(1),
    icon: z.string().optional(),
    order: z.number().default(0),
  }),
  note: z.string().optional(),
  filters: z.array(filterSchema).default([]),
})

const tableSchema = baseSchema.extend({
  layout: z.literal('table'),
  table: z.strictObject({
    rowKey: z.string(),
    defaultSort: z.array(z.string()).default([]),
    sortable: z.array(z.string()).default([]),
    search: z.array(z.string()).default([]),
    hidden: z.array(z.string()).default([]),
    /**
     * Altta sabit toplam satırı: `{ <sütun>: <ölçü> }` (kısa adlar). Sütunlar ungrouped dimension
     * olduğundan toplam ayrı bir ölçüden gelir; tanımsızsa satır çıkmaz (docs/DATA-GRID-PLAN.md).
     */
    totals: z.record(z.string(), z.string()).optional(),
  }),
})

const chartSchema = baseSchema.extend({
  layout: z.literal('chart'),
  chart: z.strictObject({
    time: z.string(),
    granularity: z.enum(GRANULARITIES).default('day'),
    series: z.array(seriesSchema).min(1),
    summary: z.array(z.string()).default([]),
    table: z.array(z.string()).default([]),
  }),
})

/**
 * Pano parçaları. `view` verilmezse sayfanın kendi view'ı; üye adları o view'a
 * göre kısa ad. Sayfa filtreleri, aynı kısa adlı üyesi olan her parçaya uygulanır
 * (`ignoreFilters` hariç). Izgara 12 kolon; `span` geniş ekranda geçerli.
 */
const widgetBase = {
  view: z.string().optional(),
  title: z.string().optional(),
  span: z.number().int().min(1).max(12).default(12),
  ignoreFilters: z.array(z.string()).default([]),
  /**
   * Çapraz filtre: `emit` parçanın tıklamayla seçim yaymasını, `receive` başkalarının
   * seçimini uygulamasını açar. `ignoreFilters` üye bazında "almaz" anlamına gelir.
   */
  crossFilter: z
    .strictObject({ emit: z.boolean().default(true), receive: z.boolean().default(true) })
    .default({ emit: true, receive: true }),
}

// Parçalar ayrı dışa açılır: rapor belgeleri (`report-document.ts`) öznitelikleri aynen kullanır.
export const kpiWidgetSchema = z.strictObject({
  ...widgetBase,
  type: z.literal('kpi'),
  measure: z.string(),
  /** Ölçüyü bu dövizin TCMB döviz satış kuruyla TL'ye çevirir. */
  fx: z.string().optional(),
  /**
   * Kaynak satırlar (Power BI drill-through): KPI'ya tıklanınca bu kolonlarla, KPI'nın
   * filtreleriyle süzülmüş ham satırlar pencerede açılır. Ölçünün kendi `filters`'ı
   * (ör. yalnız Bekliyor) `where`'de dimension eşitliği olarak yazılır: Cube'un
   * `measureFilter`'ı `ungrouped` satır sorgusunda uygulanmıyor (2026-10-27 tarayıcıda görüldü).
   */
  drill: z
    .strictObject({
      columns: z.array(z.string()).min(1),
      rowKey: z.string(),
      defaultSort: z.array(z.string()).default([]),
      where: z
        .array(z.strictObject({ member: z.string(), values: z.array(z.string()).min(1) }))
        .default([]),
    })
    .optional(),
})

// Gruplanmış küçük tablo (tümü tek sayfada), isteğe bağlı toplam satırı.
export const tableWidgetSchema = z.strictObject({
  ...widgetBase,
  type: z.literal('table'),
  dimensions: z.array(z.string()).default([]),
  measures: z.array(z.string()).min(1),
  where: z
    .array(z.strictObject({ member: z.string(), values: z.array(z.string()).min(1) }))
    .default([]),
  order: z.array(z.string()).default([]),
  totals: z.boolean().default(false),
  /**
   * Açılır matris (Power BI gibi): bu dimension'lar sırayla iç içe grup düzeyi,
   * alt toplamlı. Kalan dimension'lar yaprak kolonları; resim dimension'ı ilk
   * düzeyin etiketinde gösterilir. Ölçüler toplanabilir (`sum`) olmalı.
   * Gruplar, `order` bir ölçüyle başlıyorsa o ölçünün alt toplamına göre dizilir.
   */
  pivot: z.array(z.string()).default([]),
})

// Ham satır listesi (sayfalı). Sayfa/sıra adres çubuğunda (`page`, `page_size`, `sort`).
export const listWidgetSchema = z.strictObject({
  ...widgetBase,
  type: z.literal('list'),
  columns: z.array(z.string()).min(1),
  rowKey: z.string(),
  defaultSort: z.array(z.string()).default([]),
  sortable: z.array(z.string()).default([]),
  totals: z.array(z.string()).default([]),
  /** Panodaki ikinci liste için adres parametresi öneki (`ekstre` -> `ekstre_page`...). */
  urlKey: z
    .string()
    .regex(/^[a-z][a-z0-9]*$/)
    .optional(),
})

export const chartWidgetSchema = z.strictObject({
  ...widgetBase,
  type: z.literal('chart'),
  /** Zaman ekseni; `category` ile tam biri verilir (ayrıştırıcı denetler). */
  time: z.string().optional(),
  /**
   * Kategori ekseni (ör. banka hesabı): her değer bir çubuk, toplamı büyükten küçüğe;
   * toplamı sıfır olan değer çizilmez. `granularity` yok sayılır.
   */
  category: z.string().optional(),
  granularity: z.enum(GRANULARITIES).default('month'),
  series: z.array(seriesSchema).min(1),
  horizontal: z.boolean().default(false),
  /**
   * Kırılım boyutu (ör. firma): her ölçü çubuğu bu boyutun değerlerine göre yığılır;
   * renk boyut değerini, desen ölçüyü ayırır. Ölçüler toplanabilir (`sum`) olmalı.
   */
  breakdown: z.string().optional(),
  /**
   * Kullanıcının seçebileceği öteki kırılımlar (ör. `[bankaHesabi]`): başlıkta anahtar çıkar,
   * seçim adres çubuğunda. `breakdown` varsayılan seçenektir; o yoksa yok sayılır.
   */
  breakdownOptions: z.array(z.string()).default([]),
})

/**
 * Yaşlandırma listesi (`lib/aging.ts`): her kayıt bir satır, `amount` `date`'e göre tek bir
 * dilim sütununda. Kullanıcı günlük (`step` günlük dilimler) ile aylık arasında seçer;
 * döviz başına toplamlar en üstte. Tüm kayıtlar tek sorguda gelir, sıralama/sayfalama istemcide.
 */
export const agingWidgetSchema = z.strictObject({
  ...widgetBase,
  type: z.literal('aging'),
  date: z.string(),
  amount: z.string(),
  columns: z.array(z.string()).min(1),
  rowKey: z.string(),
  step: z.number().int().min(1).default(30),
  defaultSort: z.array(z.string()).default([]),
})

const widgetSchema = z.discriminatedUnion('type', [
  kpiWidgetSchema,
  tableWidgetSchema,
  listWidgetSchema,
  chartWidgetSchema,
  agingWidgetSchema,
])

const dashboardSchema = baseSchema.extend({
  layout: z.literal('dashboard'),
  dashboard: z.strictObject({
    widgets: z
      .array(
        z.union([
          widgetSchema,
          // Parçaları tek hücrede alt alta dizer (ör. uzun bir listenin yanındaki sol sütun).
          z.strictObject({
            type: z.literal('stack'),
            span: z.number().int().min(1).max(12).default(12),
            // true: parçalar tek kartta sekme olarak (başlıkları sekme adı).
            tabs: z.boolean().default(false),
            widgets: z.array(widgetSchema).min(1),
          }),
        ]),
      )
      .min(1),
  }),
})

/**
 * Kantar–DİA mutabakatı (`lib/kantar-dia.ts`): sayfanın view'ı DİA kalemleri, kantar
 * tarafı sabit `CanliTartim` view'ı. Kurallar kodda; meta yalnızca yer ve filtre.
 */
const reconcileSchema = baseSchema.extend({ layout: z.literal('reconcile') })

// Strict: bilinmeyen anahtar (`hiden:` gibi yazım hatası) sessizce yutulmasın.
export const reportMetaSchema = z.discriminatedUnion('layout', [
  tableSchema,
  chartSchema,
  dashboardSchema,
  reconcileSchema,
])

/**
 * View `.yml` dosyalarının editör şeması (yaml-language-server): yalnızca view
 * `meta` bloğunu tarif eder, geri kalan Cube alanları serbesttir. Dosyası
 * `cube/view-meta.schema.json`; güncelliğini `cube-model.test.ts` denetler.
 */
export function viewFileJsonSchema() {
  return z.toJSONSchema(
    z.looseObject({ views: z.array(z.looseObject({ meta: reportMetaSchema.optional() })) }),
    { io: 'input' },
  )
}

export interface ReportField {
  /** Nitelenmiş Cube üye adı, ör. `Tartim.tarih`. */
  name: string
  label: string
  type: TCubeMemberType
  numberFormat?: string
  /** `time` tipli ama takvim günü olan alanlar (`tarih`): saat gösterilmez, dönüştürülmez. */
  dateOnly: boolean
  /** Sabit para birimi kodu (`TL`, `USD`). */
  currency?: string
  /** Para birimini satırdan okuyan üye (nitelenmiş); sorguya kendiliğinden eklenir. */
  currencyMember?: string
  /** Eksi kırmızı, artı yeşil. */
  signColor: boolean
  /** Tarih aralığı filtresinden yalnız üst sınır uygulanır (`meta.upperBoundOnly`). */
  upperBoundOnly: boolean
  /** Değer bir resim yolu (`/logos/...`). */
  image: boolean
  /** Measure'lar için Cube `/meta` `aggType`'ı (`sum`, `count`, `number`, …). */
  aggType?: string
}

export interface ReportFilterDef {
  member: string
  type: 'dateRange' | 'multiSelect' | 'boolean'
  default?: string | string[]
  /** Pano parçasında: filtre durumunun okunduğu sayfa üyesi (yoksa `member`). */
  stateKey?: string
  /** `dateRange` yalnız üst sınırı uygular (dönem sonu bakiyesi: alt sınır hareketsiz hesapları düşürür). */
  upperBoundOnly?: boolean
}

export interface ReportSortItem {
  member: string
  desc: boolean
}

interface ReportBase {
  view: string
  title: string
  description?: string
  route: string
  nav: { group: string; icon?: string; order: number }
  note?: string
  filters: ReportFilterDef[]
  /** View'daki tüm dimension'lar, `includes` sırasıyla. */
  dimensions: ReportField[]
  fields: Map<string, ReportField>
}

export interface TableReport extends ReportBase {
  layout: 'table'
  rowKey: string
  defaultSort: ReportSortItem[]
  sortable: Set<string>
  search: string[]
  hidden: Set<string>
  /** Toplam satırı: nitelenmiş sütun -> nitelenmiş ölçü (meta sırasıyla). Yoksa satır yok. */
  totals?: Record<string, string>
}

export interface ChartSeries {
  measure: string
  type: 'bar' | 'line'
  axis: 'left' | 'right'
}

export interface ChartReport extends ReportBase {
  layout: 'chart'
  time: string
  granularity: Granularity
  series: ChartSeries[]
  summary: string[]
  table: string[]
}

interface WidgetBase {
  /** Panodaki sıra; React anahtarı. */
  key: string
  view: string
  title?: string
  span: number
  /** Sayfa filtrelerinden bu parçaya uygulananlar (üye parçanın view'ında). */
  filters: ReportFilterDef[]
  fields: Map<string, ReportField>
  /** `ignoreFilters` (kısa adlar): sayfa filtresi de çapraz seçim de bu üyeleri atlar. */
  ignored: ReadonlySet<string>
  crossFilter: { emit: boolean; receive: boolean }
}

export interface KpiWidget extends WidgetBase {
  type: 'kpi'
  measure: string
  fx?: string
  /** Kaynak satırlar: KPI'nın filtreleri + `drill.where` ile liste; toplamı KPI ölçüsü. */
  drill?: ListWidget
}

export interface TableWidget extends WidgetBase {
  type: 'table'
  dimensions: string[]
  measures: string[]
  where: { member: string; values: string[] }[]
  order: ReportSortItem[]
  totals: boolean
  pivot: string[]
}

export interface ListWidget extends WidgetBase {
  type: 'list'
  /** `buildReportColumns` ile aynı biçim: kolonlar + satır kimliği (gizli). */
  dimensions: ReportField[]
  rowKey: string
  defaultSort: ReportSortItem[]
  sortable: Set<string>
  totals: string[]
  /** Sabit eşitlik filtreleri: belge listesi ve KPI kaynak satırları (`kpi.drill.where`). */
  where?: { member: string; values: string[] }[]
  /** Adres parametresi öneki; yoksa `page`/`sort` öneksiz. */
  urlKey?: string
}

export interface ChartWidget extends WidgetBase {
  type: 'chart'
  /** Zaman ekseni ya da `category` kategori ekseni; tam biri dolu (tam ad). */
  time?: string
  category?: string
  granularity: Granularity
  series: ChartSeries[]
  horizontal: boolean
  /** Yığın kırılımı boyutu (tam ad). */
  breakdown?: string
  /** Seçilebilir kırılımlar (tam ad), ilki varsayılan; tek seçenekte yok. */
  breakdownOptions?: string[]
  /** Belge grafiği: sayfa filtresinden bağımsız tarih aralığı (`timeDimensions.dateRange`). */
  range?: [string, string]
}

export interface AgingWidget extends WidgetBase {
  type: 'aging'
  date: string
  amount: string
  /** Bilgi kolonları (`buildReportColumns` biçimi), satır kimliği hariç. */
  dimensions: ReportField[]
  rowKey: string
  step: number
  defaultSort: ReportSortItem[]
}

export type DashboardWidget = KpiWidget | TableWidget | ListWidget | ChartWidget | AgingWidget

/** Alt alta dizilen parçalar; tek ızgara hücresi. */
export interface StackItem {
  type: 'stack'
  key: string
  span: number
  tabs: boolean
  widgets: DashboardWidget[]
}

export interface DashboardReport extends ReportBase {
  layout: 'dashboard'
  /** Kullanıcının izni olmayan view'lara ait parçalar (ve boş kalan yığınlar) burada yoktur. */
  widgets: (DashboardWidget | StackItem)[]
}

export interface ReconcileReport extends ReportBase {
  layout: 'reconcile'
  /** Kantar view'ının alanları; kullanıcının izni yoksa (meta'da yoksa) boş. */
  kantarFields?: Map<string, ReportField>
}

export type ReportDef = TableReport | ChartReport | DashboardReport | ReconcileReport

export function toField(
  member: Cube['dimensions'][number] | Cube['measures'][number],
  isMeasure: boolean,
): ReportField {
  const meta = (member.meta ?? {}) as Record<string, unknown>
  const view = member.name.slice(0, member.name.indexOf('.'))
  // Tip tanımı `count | number` diyor; gerçek `/meta` `sum` gibi değerler de döner.
  const rawAggType = isMeasure ? (member as { aggType?: unknown }).aggType : undefined
  const aggType = typeof rawAggType === 'string' ? rawAggType : undefined
  return {
    name: member.name,
    label: typeof meta.label === 'string' ? meta.label : member.shortTitle,
    type: member.type,
    numberFormat: typeof meta.numberFormat === 'string' ? meta.numberFormat : undefined,
    dateOnly: meta.dateOnly === true,
    currency: typeof meta.currency === 'string' ? meta.currency : undefined,
    currencyMember:
      typeof meta.currencyFrom === 'string' ? `${view}.${meta.currencyFrom}` : undefined,
    signColor: meta.signColor === true,
    upperBoundOnly: meta.upperBoundOnly === true,
    image: meta.image === true,
    aggType,
  }
}

export function viewFields(cube: Cube): {
  dimensions: ReportField[]
  fields: Map<string, ReportField>
} {
  const dimensions = cube.dimensions.map((member) => toField(member, false))
  const measures = cube.measures.map((member) => toField(member, true))
  const fields = new Map([...dimensions, ...measures].map((f) => [f.name, f]))
  return { dimensions, fields }
}

/** Kısa adı nitelendirir; view'da yoksa hata fırlatır (yml yazım hatası erken yakalansın). */
function qualifier(view: string, fields: Map<string, ReportField>) {
  return (short: string): string => {
    const name = `${view}.${short}`
    if (!fields.has(name))
      throw new Error(`${view}: meta '${short}' üyesine işaret ediyor, view'da yok`)
    return name
  }
}

/** `table.totals`: sütun bir dimension, ölçü bir measure olmalı; boş nesne tanımsız sayılır. */
function parseTotals(
  raw: Record<string, string>,
  q: (short: string) => string,
  dimensions: ReadonlySet<string>,
  measures: ReadonlySet<string>,
): Record<string, string> | undefined {
  const entries = Object.entries(raw).map(([column, measure]) => [q(column), q(measure)] as const)
  for (const [column, measure] of entries) {
    if (!dimensions.has(column)) {
      throw new Error(`totals: '${shortName(column)}' bir sütun (dimension) olmalı`)
    }
    if (!measures.has(measure)) {
      throw new Error(`totals: '${shortName(measure)}' bir ölçü (measure) olmalı`)
    }
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

function parseSortToken(token: string, q: (short: string) => string): ReportSortItem {
  const desc = token.startsWith('-')
  return { member: q(desc ? token.slice(1) : token), desc }
}

type RawDashboard = z.infer<typeof dashboardSchema>['dashboard']
type RawWidget = z.infer<typeof widgetSchema>

function parseWidgets(
  raw: RawDashboard,
  page: ReportBase,
  views: ReadonlyMap<string, Cube>,
): (DashboardWidget | StackItem)[] {
  return raw.widgets.flatMap((item, index): (DashboardWidget | StackItem)[] => {
    if (item.type !== 'stack') {
      const widget = parseWidget(item, String(index), page, views)
      return widget ? [widget] : []
    }
    const widgets = item.widgets.flatMap((child, childIndex) => {
      const widget = parseWidget(child, `${index}.${childIndex}`, page, views)
      return widget ? [widget] : []
    })
    return widgets.length > 0
      ? [{ type: 'stack', key: String(index), span: item.span, tabs: item.tabs, widgets }]
      : []
  })
}

/** Parçanın sayfadan okuduğu kısım: view (kendi view'ı varsayılan), filtreler, alanlar. */
type WidgetPage = Pick<ReportBase, 'view' | 'filters' | 'fields'>

function parseWidget(
  widget: RawWidget,
  key: string,
  page: WidgetPage,
  views: ReadonlyMap<string, Cube>,
): DashboardWidget | undefined {
  const viewName = widget.view ?? page.view
  const cube = views.get(viewName)
  // Meta izne göre süzülü: izinsiz view'ın parçası sessizce düşer.
  if (!cube) return undefined
  const { fields } = viewName === page.view ? page : viewFields(cube)
  const q = qualifier(viewName, fields)
  const ignored = new Set(widget.ignoreFilters)
  const base: WidgetBase = {
    ignored,
    crossFilter: widget.crossFilter,
    key,
    view: viewName,
    title: widget.title,
    span: widget.span,
    filters: page.filters.flatMap((filter) => {
      const short = shortName(filter.member)
      const member = `${viewName}.${short}`
      return fields.has(member) && !ignored.has(short)
        ? [bindFilter(filter, member, fields.get(member))]
        : []
    }),
    fields,
  }
  switch (widget.type) {
    case 'kpi': {
      const measure = q(widget.measure)
      const drill = widget.drill && {
        ...base,
        key: `${key}.drill`,
        title: widget.title ?? fields.get(measure)?.label,
        type: 'list' as const,
        dimensions: [...new Set([...widget.drill.columns, widget.drill.rowKey].map(q))].map(
          (name) => fields.get(name) as ReportField,
        ),
        rowKey: q(widget.drill.rowKey),
        defaultSort: widget.drill.defaultSort.map((token) => parseSortToken(token, q)),
        sortable: new Set(widget.drill.columns.map(q)),
        totals: [measure],
        where: widget.drill.where.map((item) => ({ ...item, member: q(item.member) })),
      }
      return { ...base, type: 'kpi', measure, fx: widget.fx, ...(drill && { drill }) }
    }
    case 'table':
      for (const level of widget.pivot) {
        if (!widget.dimensions.includes(level)) {
          throw new Error(`${page.view}: pivot '${level}' parçanın dimensions listesinde yok`)
        }
      }
      return {
        ...base,
        type: 'table',
        dimensions: widget.dimensions.map(q),
        measures: widget.measures.map(q),
        where: widget.where.map((item) => ({ member: q(item.member), values: item.values })),
        order: widget.order.map((token) => parseSortToken(token, q)),
        totals: widget.totals,
        pivot: widget.pivot.map(q),
      }
    case 'aging':
      return {
        ...base,
        type: 'aging',
        date: q(widget.date),
        amount: q(widget.amount),
        dimensions: widget.columns.map((name) => fields.get(q(name)) as ReportField),
        rowKey: q(widget.rowKey),
        step: widget.step,
        defaultSort: widget.defaultSort.map((token) => parseSortToken(token, q)),
      }
    case 'list': {
      const rowKey = q(widget.rowKey)

      const columns = [...new Set([...widget.columns.map(q), rowKey])]
      return {
        ...base,
        type: 'list',
        dimensions: columns.map((name) => fields.get(name) as ReportField),
        rowKey,
        defaultSort: widget.defaultSort.map((token) => parseSortToken(token, q)),
        sortable: new Set(widget.sortable.map(q)),
        totals: widget.totals.map(q),
        ...(widget.urlKey && { urlKey: widget.urlKey }),
      }
    }
    case 'chart':
      if (!widget.time === !widget.category) {
        throw new Error(
          `${page.view}: grafik parçasında 'time' ya da 'category'den tam biri olmalı`,
        )
      }
      return {
        ...base,
        type: 'chart',
        ...(widget.time && { time: q(widget.time) }),
        ...(widget.category && { category: q(widget.category) }),
        granularity: widget.granularity,
        series: widget.series.map((series) => ({ ...series, measure: q(series.measure) })),
        horizontal: widget.horizontal,
        ...(widget.breakdown && { breakdown: q(widget.breakdown) }),
        ...(widget.breakdown &&
          widget.breakdownOptions.length > 0 && {
            breakdownOptions: [...new Set([widget.breakdown, ...widget.breakdownOptions].map(q))],
          }),
      }
  }
}

/**
 * Belge widget düğümü (`report-document.ts` öznitelikleri) -> pano parçası. Belgede sayfa view'ı
 * yoktur: `view` zorunlu, `pageFilters` belge filtreleridir. İzinsiz view `undefined` döner.
 */
export function parseDocumentWidget(
  widget: RawWidget & { view: string },
  key: string,
  pageFilters: ReportFilterDef[],
  views: ReadonlyMap<string, Cube>,
): DashboardWidget | undefined {
  return parseWidget(widget, key, { view: '', filters: pageFilters, fields: new Map() }, views)
}

/**
 * `views`: aynı meta'daki tüm view'lar — pano parçaları başka view'lara işaret
 * edebilir. Verilmezse yalnızca bu view bilinir.
 */
export function parseReport(
  cube: Cube,
  views: ReadonlyMap<string, Cube> = new Map([[cube.name, cube]]),
): ReportDef {
  const raw = reportMetaSchema.parse(cube.meta)
  const { dimensions, fields } = viewFields(cube)
  const q = qualifier(cube.name, fields)

  const base: ReportBase = {
    view: cube.name,
    title: cube.title,
    description: cube.description,
    route: raw.route,
    nav: raw.nav,
    note: raw.note,
    filters: raw.filters.map((filter) => ({
      member: q(filter.member),
      type: filter.type,
      default: filter.default,
    })),
    dimensions,
    fields,
  }
  if (base.filters.filter((filter) => filter.type === 'dateRange').length > 1) {
    throw new Error(`${cube.name}: en fazla bir dateRange filtresi olabilir`)
  }
  for (const filter of base.filters) {
    if (filter.default === undefined) continue
    const valid =
      filter.type === 'dateRange'
        ? typeof filter.default === 'string'
        : filter.type === 'multiSelect' && Array.isArray(filter.default)
    if (!valid) {
      throw new Error(
        `${cube.name}: '${shortName(filter.member)}' filtresinin varsayılanı geçersiz`,
      )
    }
  }

  if (raw.layout === 'dashboard') {
    return { ...base, layout: 'dashboard', widgets: parseWidgets(raw.dashboard, base, views) }
  }
  if (raw.layout === 'reconcile') {
    const kantar = views.get(KANTAR_VIEW)
    return { ...base, layout: 'reconcile', kantarFields: kantar && viewFields(kantar).fields }
  }
  if (raw.layout === 'table') {
    const totals =
      raw.table.totals &&
      parseTotals(
        raw.table.totals,
        q,
        new Set(cube.dimensions.map((member) => member.name)),
        new Set(cube.measures.map((member) => member.name)),
      )
    return {
      ...base,
      layout: 'table',
      rowKey: q(raw.table.rowKey),
      defaultSort: raw.table.defaultSort.map((token) => parseSortToken(token, q)),
      sortable: new Set(raw.table.sortable.map(q)),
      search: raw.table.search.map(q),
      hidden: new Set(raw.table.hidden.map(q)),
      ...(totals && { totals }),
    }
  }
  return {
    ...base,
    layout: 'chart',
    time: q(raw.chart.time),
    granularity: raw.chart.granularity,
    series: raw.chart.series.map((series) => ({ ...series, measure: q(series.measure) })),
    summary: raw.chart.summary.map(q),
    table: raw.chart.table.map(q),
  }
}

export interface ReportError {
  view: string
  message: string
}

function parseAll(meta: Meta): { reports: ReportDef[]; errors: ReportError[] } {
  const reports: ReportDef[] = []
  const errors: ReportError[] = []
  const views = new Map(
    meta.cubes.filter((cube) => cube.type === 'view').map((cube) => [cube.name, cube]),
  )
  for (const cube of views.values()) {
    if (!cube.meta) continue
    try {
      reports.push(parseReport(cube, views))
    } catch (error) {
      const message = error instanceof z.ZodError ? z.prettifyError(error) : String(error)
      errors.push({ view: cube.name, message })
    }
  }
  return { reports: reports.sort((a, b) => a.nav.order - b.nav.order), errors }
}

/**
 * Meta'daki rapor view'larını menü sırasıyla döner. `meta` taşımayan view rapor
 * değildir, atlanır; bozuk meta'lı view konsola yazılıp atlanır — tek bir hatalı
 * `.yml` bütün menüyü düşürmesin.
 */
export function parseReports(meta: Meta): ReportDef[] {
  const { reports, errors } = parseAll(meta)
  for (const error of errors) console.error(`Rapor tanımı geçersiz: ${error.view}`, error.message)
  return reports
}

/** Menüden düşen (bozuk meta'lı) view'lar — superadmin uyarısı için. */
export function parseReportErrors(meta: Meta): ReportError[] {
  return parseAll(meta).errors
}

/** Sütun filtresi giriş tipi — Cube tipinden türetilir. */
export function filterKind(field: ReportField): FilterFieldKind {
  switch (field.type) {
    case 'time':
      return field.dateOnly ? 'date' : 'datetime'
    case 'number':
      return 'numeric'
    case 'boolean':
      return 'bool'
    default:
      return 'text'
  }
}

/** URL parametre adı: üyenin kısa adı (`Tartim.kantarId` -> `kantarId`). */
export function shortName(member: string): string {
  return member.slice(member.indexOf('.') + 1)
}

/** Sayfa filtresini bir view'ın üyesine bağlar; durum anahtarı sayfa üyesinde kalır. */
export function bindFilter(
  filter: ReportFilterDef,
  member: string,
  field: ReportField | undefined,
): ReportFilterDef {
  return {
    ...filter,
    member,
    stateKey: filter.member,
    ...(field?.upperBoundOnly && filter.type === 'dateRange' && { upperBoundOnly: true }),
  }
}

/** Sağa yaslanacak (biçimli sayı) alan mı. Biçimsiz sayılar kod gibi ham gösterilir. */
export function isFormattedNumber(field: ReportField): boolean {
  return field.type === 'number' && field.numberFormat !== undefined
}

/** Alanın para birimi: sabit ya da satırdaki döviz üyesinden. */
export function fieldCurrency(field: ReportField, row?: Record<string, unknown>) {
  if (field.currency) return field.currency
  const code = field.currencyMember ? row?.[field.currencyMember] : undefined
  return typeof code === 'string' ? code : undefined
}

/** Sorguya eklenmesi gereken döviz üyeleri (satırdan para birimi okuyan alanlar için). */
export function currencyMembers(
  fields: ReadonlyMap<string, ReportField>,
  members: string[],
): string[] {
  return [...new Set(members.flatMap((member) => fields.get(member)?.currencyMember ?? []))]
}

/** Cube `sum`/`number` değerlerini string olarak da döndürebilir (bigint/numeric). */
export function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

/**
 * Ekran metni. Biçimsiz sayı ham kalır: anlamı bilinmeyen kod alanları yorumlanmaz
 * (AGENTS.md §1). `row` verilirse satırdaki döviz simgesi eklenir.
 */
export function formatFieldValue(
  field: ReportField,
  value: unknown,
  row?: Record<string, unknown>,
): string {
  switch (field.type) {
    case 'time':
      return field.dateOnly
        ? formatDateOnly(value as string | null)
        : formatCubeDateTime(value as string | null)
    case 'boolean':
      return formatBoolean(value as boolean | null)
    case 'number':
      return field.numberFormat
        ? withCurrency(
            formatNumberPattern(toNumber(value), field.numberFormat),
            fieldCurrency(field, row),
          )
        : formatText(value as number | null)
    default:
      return formatText(value as string | null)
  }
}

/** Toplam satırı: sütun adı (nitelenmiş) -> ekran metni. Döviz kırılımında her döviz ayrı satır. */
export interface TotalsRow {
  key: string
  cells: Record<string, string>
}

/**
 * `table.totals` sorgusunun satırları -> ekran metni. Biçim sütunun `numberFormat`'ıdır
 * (yoksa ölçününki); para birimi ölçünün döviz üyesinden, satırdan okunur.
 */
export function formatTotalsRows(
  report: Pick<TableReport, 'totals' | 'fields'>,
  rows: readonly Record<string, unknown>[],
): TotalsRow[] {
  const { totals, fields } = report
  if (!totals) return []
  return rows.map((row, index) => ({
    key: String(index),
    cells: Object.fromEntries(
      Object.entries(totals).flatMap(([column, measure]) => {
        const measureField = fields.get(measure)
        if (!measureField) return []
        const numberFormat = fields.get(column)?.numberFormat ?? measureField.numberFormat
        return [[column, formatFieldValue({ ...measureField, numberFormat }, row[measure], row)]]
      }),
    ),
  }))
}

/** Excel hücresi: biçimli sayılar ham `number` (biçimi Excel uygular), gerisi ekran metni. */
export function exportFieldValue(field: ReportField, value: unknown): string | number {
  if (isFormattedNumber(field)) return toNumber(value) ?? ''
  return formatFieldValue(field, value)
}
